import { useEffect, useState } from 'react';
import classNames from './UnitData.module.scss';
import { useAdminTranslation } from '@/admin/hooks/useAdminTranslation';
import SVG from 'react-inlinesvg';
import { gameContentService } from '@/school-management/services/gameContent';
import { studentDetailsService } from '@/school-management/services/studentDetails';
import {
  StudentProgress,
  StudentDetails,
  UnitProgress,
} from '@/school-management/types/response/studentDetails';
import { UnitsTopics, CanDoAnswer } from '@/school-management/types/response/gameContent';
import commonUtils from '@/school-management/utils/common';

import AppLoaderCircle from '@/components/AppLoaderCircle';
import TopicAccordion, { TopicAccordionProps } from './TopicAccordion/TopicAccordion';

import trophy from '@/assets/svg/trophy.svg';
import medal from '@/assets/svg/medal-gold.svg';
import star from '@/assets/svg/star-rating.svg';

interface UnitDataProps {
  schoolId: string;
  studentId: string;
  studentDetails: StudentDetails;
  unit: UnitProgress;
}

const UnitData = ({ schoolId, studentId, studentDetails, unit }: UnitDataProps) => {
  const { t } = useAdminTranslation();
  const [topics, setTopics] = useState<TopicAccordionProps[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    let isActive = true;

    const init = async () => {
      if (
        unit?.unitId &&
        schoolId &&
        studentId &&
        studentDetails?.progress?.level &&
        studentDetails?.studentData?.nativeLanguage
      ) {
        setIsLoading(true);

        const fetchs = [
          new Promise(resolve => {
            const getCanDoMapper = async () => {
              let canDoMapper = new Map();
              try {
                const response =
                  (
                    await gameContentService.getCanDoAnswers(
                      studentDetails.studentData.fromToLanguage
                    )
                  )?.data?.data || [];
                canDoMapper = response.reduce((mapper, canDo) => {
                  mapper.set(canDo.id, canDo);
                  return mapper;
                }, new Map());
              } catch (ex) {}
              resolve(canDoMapper);
            };
            getCanDoMapper();
          }),
          new Promise(resolve => {
            const getTopics = async () => {
              let topics: UnitsTopics = [];
              try {
                const response =
                  (
                    await gameContentService.getUnitTopics(
                      studentDetails.studentData.fromToLanguage,
                      studentDetails.progress.level,
                      unit.unitId
                    )
                  )?.data?.data || [];
                topics = response
                  .filter(topic => topic.activitiesInTopic > 0)
                  .sort((topic1, topic2) =>
                    topic1.order < topic2.order ? -1 : topic1.order < topic2.order ? 1 : 0
                  );
              } catch (ex) {
                commonUtils.showToast(t('studentDataModal.unitData.somethingWentWrong'), {
                  type: 'error',
                });
              }
              resolve(topics);
            };
            getTopics();
          }),
          new Promise(resolve => {
            const getProgress = async () => {
              try {
                const response = (
                  await studentDetailsService.getStudentProgress(schoolId, studentId)
                )?.data?.data;
                resolve(response);
              } catch (ex) {}

              resolve(null);
            };
            getProgress();
          }),
        ];

        const initData = await Promise.all(fetchs);

        if (!isActive) {
          return;
        }

        const canDoMapper = initData[0] as Map<number, CanDoAnswer>;
        const topicsList = initData[1] as UnitsTopics;
        const progress = initData[2] as StudentProgress | null;
        const unitProgress = progress?.unit?.topicsOverviewPerUnit?.[unit.unitId] || null;

        const topics: TopicAccordionProps[] = topicsList.map(topic => {
          const topicProgress = unitProgress?.[topic.id] || null;

          const topicId: number = topic.id;
          const topicName: string = topic.name;
          const totalActivities: number = topic.activitiesInTopic;
          const completedActivities: number = topicProgress?.completedActivitiesCount || 0;
          const completionPercentage: number = commonUtils.calcPercentage(
            completedActivities,
            totalActivities
          );
          const activitiesProgress =
            progress?.topic?.activitiesOverviewPerTopic?.[topic.id] || null;

          const canDoAnswerId: number = topicProgress?.canDo?.userAnswered || 0;
          const canDoAnswer: CanDoAnswer | null = canDoMapper.get(canDoAnswerId) || null;
          const canDoStatement: string = topic.canDoStatement;

          return {
            unitId: unit.unitId,
            topicId,
            topicName,
            totalActivities,
            completedActivities,
            completionPercentage,
            activitiesProgress,
            canDoAnswerId,
            canDoAnswer,
            canDoStatement,
            level: studentDetails.progress.level,
            userNativeLanguage: studentDetails.studentData.nativeLanguage,
            fromToLanguage: studentDetails.studentData.fromToLanguage,
          };
        });

        setTopics(topics);
        setIsLoading(false);
      }
    };

    init();

    return () => {
      isActive = false;
    };
  }, [unit, schoolId, studentId, studentDetails, t]);

  const accordionItems = topics.map((topic, index) => {
    return <TopicAccordion key={index} {...topic} />;
  });

  if (isLoading) {
    return (
      <div className={classNames.loader}>
        <AppLoaderCircle />
      </div>
    );
  }

  return (
    <div className={classNames.unitData}>
      <div className={classNames.header}>
        <div className={classNames.texts}>
          <div className={classNames.nameAndPercentage}>
            <span className={classNames.name}>{unit?.unitName || ''}</span>
            <span className={classNames.percentage}>{`${unit?.progress || 0}%`}</span>
          </div>
          <div className={classNames.user}>
            <span>{`${studentDetails?.studentData?.firstName || ''} ${studentDetails?.studentData?.lastName || ''}`}</span>
            <div className={classNames.detailSeperator}></div>
            <span>{`${t('studentDataModal.unitData.level')} ${studentDetails?.progress?.level || 0}`}</span>
          </div>
        </div>
      </div>
      <div className={classNames.details}>
        <div className={classNames.box}>
          <SVG src={trophy} />
          <span>{`${unit?.completedTopics || 0}/${unit?.totalTopics || 0} ${t('studentDataModal.unitData.topics')}`}</span>
        </div>
        <div className={classNames.box}>
          <SVG src={medal} />
          <span>{`${unit?.completedActivities || 0}/${unit?.totalActivites || 0} ${t('studentDataModal.unitData.activities')}`}</span>
        </div>
        <div className={classNames.box}>
          <SVG src={star} />
          <span>{`${unit?.achievedStars || 0}/${unit?.totalStars || 0} ${t('studentDataModal.unitData.stars')}`}</span>
        </div>
      </div>
      <div className={classNames.accordion}>{accordionItems}</div>
    </div>
  );
};

export default UnitData;
